import { Component, Input, Output, EventEmitter, ViewChild, NgZone, ChangeDetectorRef, ElementRef, OnDestroy, OnChanges } from '@angular/core';
import { BlogDetailPanelComponent } from '../blog-detail-panel/blog-detail-panel.component';
import { Blog, BlogRevision } from '../../types';

@Component({
  standalone: false,
  selector: 'mkt-review-queue',
  template: `
    <div class="mkt-review-queue"
         [class.mkt-review-queue--resizing]="resizing"
         [style.grid-template-columns]="gridColumns">
      <!-- Card List -->
      <main class="mkt-review-queue__center">
        <div *ngIf="blogs.length === 0" class="mkt-review-queue__empty">
          <i class="fa-solid fa-circle-check"></i>
          <p>All caught up! No posts awaiting review.</p>
        </div>
        <div class="mkt-review-queue__card-list">
          <div *ngFor="let blog of blogs; let i = index"
               class="mkt-review-queue__card-wrapper"
               [style.animation-delay.ms]="i * 100">
            <mkt-content-card
              [blog]="blog"
              [selected]="blog.ID === selectedBlogId"
              [showActions]="true"
              [revisionCount]="getRevisionCount(blog.ID)"
              [latestFeedback]="getLatestFeedback(blog.ID)"
              [brandVoiceScore]="getBrandVoiceScore(blog.ID)"
              (select)="selectBlog.emit($event)"
              (statusChange)="statusChange.emit($event)"
              (deleteBlog)="deleteBlog.emit($event)">
            </mkt-content-card>
          </div>
        </div>
      </main>

      <!-- Resize Handle -->
      <div class="mkt-review-queue__resize-handle"
           *ngIf="selectedBlog"
           (mousedown)="onResizeStart($event)">
        <div class="mkt-review-queue__resize-grip"></div>
      </div>

      <!-- Blog Detail Panel -->
      <mkt-blog-detail-panel
        *ngIf="selectedBlog"
        [blog]="selectedBlog"
        [allRevisions]="allRevisions"
        (close)="closeBlogDetail.emit()"
        (statusChange)="statusChange.emit($event)"
        (deleteBlog)="deleteBlog.emit($event)"
        (blogSaved)="blogSaved.emit($event)"
        (revisionRequested)="revisionRequested.emit($event)"
        #blogDetailPanel
        class="mkt-review-queue__panel">
      </mkt-blog-detail-panel>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      flex: 1;
      overflow: hidden;
    }

    .mkt-review-queue {
      display: grid;
      height: 100%;
      overflow: hidden;
      position: relative;
      transition: grid-template-columns 0.3s ease;
    }
    .mkt-review-queue--resizing {
      transition: none !important;
    }

    .mkt-review-queue__center {
      padding: 16px;
      overflow-y: auto;
      min-width: 200px;
    }

    .mkt-review-queue__card-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .mkt-review-queue__card-wrapper {
      opacity: 0;
      animation: mktCardIn 0.3s ease forwards;
    }

    .mkt-review-queue__empty {
      text-align: center;
      padding: 60px 20px;
      color: var(--mj-text-disabled);
    }
    .mkt-review-queue__empty i {
      font-size: 2.5rem;
      margin-bottom: 12px;
      display: block;
      color: var(--mj-status-success);
    }
    .mkt-review-queue__empty p {
      font-size: 0.9rem;
      margin: 0;
    }

    /* Resize Handle */
    .mkt-review-queue__resize-handle {
      cursor: col-resize;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      transition: background 0.15s;
    }
    .mkt-review-queue__resize-handle:hover {
      background: color-mix(in srgb, var(--mj-brand-primary) 8%, transparent);
    }
    .mkt-review-queue__resize-grip {
      width: 2px;
      height: 32px;
      border-radius: 1px;
      background: var(--mj-border-default);
      transition: background 0.15s;
    }
    .mkt-review-queue__resize-handle:hover .mkt-review-queue__resize-grip {
      background: var(--mj-brand-primary);
    }

    /* Side Panel */
    .mkt-review-queue__panel {
      border-left: 1px solid var(--mj-border-default);
      animation: mktSlideIn 0.25s ease forwards;
    }

    @keyframes mktCardIn {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes mktSlideIn {
      from { transform: translateX(20px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `]
})
export class ReviewQueueComponent implements OnDestroy {
  @Input() blogs: Blog[] = [];
  @Input() selectedBlogId: string | null = null;
  @Input() selectedBlog: Blog | null = null;
  @Input() allRevisions: BlogRevision[] = [];
  @Input() brandVoiceScores: Record<string, number> = {};

  @Output() selectBlog = new EventEmitter<string>();
  @Output() closeBlogDetail = new EventEmitter<void>();
  @Output() statusChange = new EventEmitter<{ blogId: string; newStatus: string }>();
  @Output() deleteBlog = new EventEmitter<string>();
  @Output() blogSaved = new EventEmitter<string>();
  @Output() revisionRequested = new EventEmitter<{ blogId: string; feedback: string; feedbackOnly: boolean; critiqueType: string }>();

  @ViewChild('blogDetailPanel') blogDetailPanel?: BlogDetailPanelComponent;

  // Right panel resize
  rightPanelWidth = 380;
  resizing = false;
  private readonly RIGHT_MIN = 320;
  private readonly RIGHT_MAX_PCT = 0.6;
  private startX = 0;
  private startWidth = 0;

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  get gridColumns(): string {
    return this.selectedBlogId
      ? `1fr 6px ${this.rightPanelWidth}px`
      : '1fr';
  }

  getRevisionCount(blogId: string): number {
    return this.allRevisions.filter(r => r.BlogID === blogId).length;
  }

  getLatestFeedback(blogId: string): string {
    const rev = this.allRevisions.find(r => r.BlogID === blogId && r.Feedback);
    return rev?.Feedback || '';
  }

  getBrandVoiceScore(blogId: string): number | null {
    return this.brandVoiceScores[blogId] ?? null;
  }

  onResizeStart(e: MouseEvent) {
    e.preventDefault();
    this.resizing = true;
    this.startX = e.clientX;
    this.startWidth = this.rightPanelWidth;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onResizeMove);
      document.addEventListener('mouseup', this.onResizeEnd);
    });
  }

  private onResizeMove = (e: MouseEvent) => {
    const delta = this.startX - e.clientX;
    const host = this.elementRef.nativeElement.offsetWidth;
    this.rightPanelWidth = Math.min(
      host * this.RIGHT_MAX_PCT,
      Math.max(this.RIGHT_MIN, this.startWidth + delta)
    );
    const grid = this.elementRef.nativeElement.querySelector('.mkt-review-queue') as HTMLElement;
    if (grid) {
      grid.style.gridTemplateColumns = `1fr 6px ${this.rightPanelWidth}px`;
    }
  };

  private onResizeEnd = () => {
    this.resizing = false;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
    this.ngZone.run(() => this.cdr.detectChanges());
  };

  ngOnDestroy() {
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
  }
}
